import { motion } from 'framer-motion';
import { fadeUp, staggeredFadeIn } from '../../utils/animations';
import { Target, ScanEye, Lock, FileText, Link2 } from 'lucide-react';

const steps = [
    { num: '01', title: 'Diagnostic Consultation', body: "This is not a sales call. We learn your business, your team setup, your current systems, and where the real gaps are. We tell you honestly what we think you need - even if it's not us." },
    { num: '02', title: 'Role Design & Scope Clarity', body: "Before we source a single candidate, we define the role. Tasks, systems, success metrics, communication expectations. You can't hire well for a role that isn't defined." },
    { num: '03', title: 'Candidate Search & Screening', body: 'We source candidates and run them through skills testing and behavioral screening. No resume flooding. No guesswork. Only candidates who pass move forward.' },
    { num: '04', title: 'Structured Interview Process', body: "We lead the interviews and stress-test candidates on your behalf. You get an honest pros and cons breakdown - not a sales pitch for every candidate we've found." },
    { num: '05', title: 'Offer Guidance', body: 'We help you structure the offer the right way - compensation, scope, communication standards, and expectations - so the relationship starts on solid ground.' },
    { num: '06', title: 'Structured Onboarding Roadmap', body: "We provide the framework for the first 90 days. Your VA knows what they're doing, what success looks like, and how to communicate when they're stuck. You don't have to build this from scratch." },
    { num: '07', title: 'Monthly Timeclock Audit', body: "Every month we review your VA's time logs - checking for drift, inefficiency, and accountability issues. You get a plain-English summary and any coaching recommendations. This is proactive oversight, not reactive damage control." },
    { num: '08', title: 'Dedicated Support Access', body: 'One person. Direct access via Slack, phone, or text. No support tickets. No waiting. If something comes up, you reach a real human who knows your business.' },
    { num: '09', title: 'Annual Business Review', body: "Once a year we sit down with you - VA performance, systems maturity, documentation progress, compensation review, and your future hiring roadmap. This is a long-term partnership, not a one-time placement." },
];

const pillars = [
    { icon: Target, label: 'Hiring Discipline' },
    { icon: ScanEye, label: 'Ongoing Oversight' },
    { icon: Lock, label: 'Accountability Structure' },
    { icon: FileText, label: 'Documentation Progress' },
    { icon: Link2, label: 'Long-Term Operational Continuity' },
];

export function StepsTimeline() {
    return (
        <section className="bg-white py-24 md:py-32" data-testid="steps-section">
            <div className="max-w-3xl mx-auto px-6 lg:px-12">
                <motion.div {...fadeUp} className="mb-14">
                    <h2 className="font-montserrat font-bold text-3xl md:text-4xl text-navy-900 tracking-tight">
                        How It Works, Start to Finish.
                    </h2>
                </motion.div>

                <div className="space-y-0">
                    {steps.map((step, i) => (
                        <motion.div
                            key={step.num}
                            {...staggeredFadeIn(i * 0.05)}
                            className="relative pl-16 pb-12 last:pb-0"
                            data-testid={`step-${step.num}`}
                        >
                            {i < steps.length - 1 && (
                                <div className="absolute left-[19px] top-10 w-px h-[calc(100%-24px)] bg-gray-200" />
                            )}
                            <div className="absolute left-0 top-0 w-10 h-10 rounded-full bg-navy-900 flex items-center justify-center">
                                <span className="text-gold-400 text-xs font-bold font-inter">{step.num}</span>
                            </div>
                            <h3 className="font-montserrat font-bold text-lg text-navy-900 mb-2 pt-1.5">{step.title}</h3>
                            <p className="text-[#6B7280] text-sm leading-relaxed">{step.body}</p>
                        </motion.div>
                    ))}
                </div>
            </div>
        </section>
    );
}

export function InfrastructureCallout() {
    return (
        <section className="bg-[#F8F9FA] py-24 md:py-32" data-testid="infrastructure-callout">
            <div className="max-w-4xl mx-auto px-6 lg:px-12">
                <motion.div {...fadeUp} className="text-center mb-12">
                    <h2 className="font-montserrat font-bold text-3xl md:text-4xl text-navy-900 mb-4 tracking-tight">
                        This Is Infrastructure. Not Labor.
                    </h2>
                    <p className="text-[#6B7280] text-lg max-w-2xl mx-auto leading-relaxed">
                        When you work with White Glove Hiring, you're not renting a worker. You're building an operational system that holds up over time - one that doesn't break when someone leaves, doesn't depend on you to run it, and gets stronger the longer it runs.
                    </p>
                </motion.div>

                <div className="flex flex-wrap justify-center gap-3 md:gap-4 mb-12">
                    {pillars.map((pillar, i) => (
                        <motion.div
                            key={pillar.label}
                            {...staggeredFadeIn(i * 0.08)}
                            className="flex items-center gap-2 bg-white border border-gray-100 rounded-sm px-5 py-3 shadow-sm hover:shadow-md hover:border-gold-200 transition-all duration-300"
                        >
                            <pillar.icon size={16} className="text-gold-400 flex-shrink-0" strokeWidth={1.5} />
                            <span className="font-inter font-semibold text-navy-900 text-sm whitespace-nowrap">{pillar.label}</span>
                        </motion.div>
                    ))}
                </div>

                <motion.p {...fadeUp} className="text-center text-navy-900 font-montserrat font-bold text-xl italic">
                    "That is the difference between a VA and leverage."
                </motion.p>
            </div>
        </section>
    );
}
